/**
 * Metric registry of the Kennzahlen hub (#2175 B1). One entry per cockpit
 * metric: label, basis caption, endpoint, unit, chart, delta direction, scope
 * support, breakdown labels and the adapter of its response.
 *
 * Port of mobile-management/src/features/metrics/registry.ts (#1989 G1). The
 * entries, their order and their wording are the phone's; the message ids move
 * from `management.metrics.*` to `analytics.metrics.*` and take the phone's
 * catalog text in all six locales. Dropped on the web: `cacheKey` (no tab hooks
 * to share with) and `screen` (every metric opens the generic detail).
 * registry.test.ts carries the phone's assertions. A follow-up extracts a
 * shared package so the two copies cannot drift.
 *
 * Left out, as on the phone: `financial/subsidies` and the
 * `portfolio-documentation/scopes` side endpoint.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  adaptAbsenceRates,
  adaptAbsenceRatesSummary,
  adaptBilledTrend,
  adaptActivityMinutes,
  adaptCareRatio,
  adaptCashIn,
  adaptCockpitEnvelope,
  adaptCockpitEnvelopeNoParts,
  adaptCockpitEnvelopeParts,
  adaptContracts,
  adaptEnrollmentMovement,
  adaptForecastCumulative,
  adaptLiquidity,
  adaptOutstandingTrend,
  adaptPaymentMethods,
  adaptPayments,
  adaptProcessingTime,
  adaptViolations,
  adaptWorkflowItems,
} from "./adapters";
import type {
  MetricDefinition,
  MetricPoint,
  MetricRequestParams,
  MetricSectionId,
} from "./types";
import { shiftMonth } from "./period";

const BOTH = { location: true, group: true } as const;
const LOCATION_ONLY = { location: true, group: false } as const;
const NONE = { location: false, group: false } as const;

/** Drop the breakdown of an adapter whose parts are not parts of the value. */
function withoutParts(adapt: (r: unknown) => MetricPoint[]) {
  return (response: unknown): MetricPoint[] =>
    adapt(response).map(({ breakdown: _b, ...p }) => p);
}

export const METRIC_SECTIONS: readonly {
  id: MetricSectionId;
  label: MessageDescriptor;
}[] = [
  {
    id: 'occupancy',
    label: msg({ id: 'analytics.metrics.section.occupancy', message: 'Occupancy' }),
  },
  { id: 'team', label: msg({ id: 'analytics.metrics.section.team', message: 'Team' }) },
  {
    id: 'children',
    label: msg({ id: 'analytics.metrics.section.children', message: 'Children' }),
  },
  {
    id: 'finance',
    label: msg({ id: 'analytics.metrics.section.finance', message: 'Finance' }),
  },
  {
    id: 'workflows',
    label: msg({ id: 'analytics.metrics.section.workflows', message: 'Workflows' }),
  },
];

const ABSENCE_TYPE_LABELS: Record<string, MessageDescriptor> = {
  SICK: msg({ id: 'analytics.metrics.absenceType.sick', message: 'Sick' }),
  ACCIDENT: msg({ id: 'analytics.metrics.absenceType.accident', message: 'Accident' }),
  CHILD_SICK: msg({ id: 'analytics.metrics.absenceType.childSick', message: 'Child sick' }),
  VACATION: msg({ id: 'analytics.metrics.absenceType.vacation', message: 'Vacation' }),
  PERSONAL: msg({ id: 'analytics.metrics.absenceType.personal', message: 'Personal' }),
  TRAINING: msg({ id: 'analytics.metrics.absenceType.training', message: 'Training' }),
  COMP_TIME: msg({ id: 'analytics.metrics.absenceType.compTime', message: 'Comp time' }),
  OTHER: msg({ id: 'analytics.metrics.absenceType.other', message: 'Other' }),
};

const NEW_ENDED_LABELS: Record<string, MessageDescriptor> = {
  new: msg({ id: 'analytics.metrics.part.new', message: 'New' }),
  ended: msg({ id: 'analytics.metrics.part.ended', message: 'Ended' }),
};

/** Ages of open amounts (`financial/payments` bucket labels). */
const AGING_LABELS: Record<string, MessageDescriptor> = {
  '0-30': msg({ id: 'analytics.metrics.aging.d0_30', message: '0\u201330 days' }),
  '31-60': msg({ id: 'analytics.metrics.aging.d31_60', message: '31\u201360 days' }),
  '61-90': msg({ id: 'analytics.metrics.aging.d61_90', message: '61\u201390 days' }),
  '90+': msg({ id: 'analytics.metrics.aging.d90plus', message: 'Over 90 days' }),
};

/** Contract payment methods (`contracts.payment_method`, null as `unknown`). */
const PAYMENT_METHOD_LABELS: Record<string, MessageDescriptor> = {
  bank_transfer: msg({
    id: 'analytics.metrics.method.bankTransfer',
    message: 'Bank transfer',
  }),
  sepa: msg({ id: 'analytics.metrics.method.directDebit', message: 'Direct debit' }),
  cash: msg({ id: 'analytics.metrics.method.cash', message: 'Cash' }),
  credit_card: msg({ id: 'analytics.metrics.method.card', message: 'Credit card' }),
  unknown: msg({ id: 'analytics.metrics.method.unknown', message: 'Not set' }),
};

/** The whole calendar year of `to` (the year-end forecast, D9a). */
function calendarYearOf(_from: string, to: string): MetricRequestParams[] {
  const y = to.slice(0, 4);
  // A future year (the "Next year" preset, #2174) needs the endpoint's
  // target_year, or the fast read projects only the current year.
  const future = Number(y) > new Date().getFullYear();
  return [{ from: `${y}-01`, to: `${y}-12`, ...(future ? { target_year: y } : {}) }];
}

/** The twelve months from today: the liquidity endpoint ignores from/to anyway. */
function nextTwelveMonths(): MetricRequestParams[] {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return [{ from: month, to: shiftMonth(month, 11) }];
}

const PAYER_LABELS: Record<string, MessageDescriptor> = {
  parent: msg({ id: 'analytics.metrics.part.parents', message: 'Parents' }),
  subsidy: msg({ id: 'analytics.metrics.part.offices', message: 'Subsidy offices' }),
};

export const METRICS: readonly MetricDefinition[] = [
  // --- Occupancy ------------------------------------------------------------
  {
    id: 'utilization',
    section: 'occupancy',
    label: msg({ id: 'analytics.metrics.utilization', message: 'Occupancy' }),
    basis: msg({
      id: 'analytics.metrics.utilization.basisAvg',
      message: 'Weighted children per weighted place, average of the months.',
    }),
    path: '/api/v1/cockpit/occupancy/utilization',
    unit: 'percent',
    aggregate: 'mean',
    keywords: ['belegung', 'auslastung', 'pl\u00e4tze', 'occupancy', 'utilisation', 'places'],
    chart: 'line',
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    adapt: adaptCockpitEnvelopeNoParts,
    detailRoute: 'groups',
  },
  {
    id: 'care_ratio',
    section: 'occupancy',
    label: msg({ id: 'analytics.metrics.careRatio', message: 'Care ratio' }),
    basis: msg({
      id: 'analytics.metrics.careRatio.basisAvg',
      message: 'Children per staff member on shift, average of the groups and months.',
    }),
    path: '/api/v1/cockpit/occupancy/care-ratio',
    unit: 'ratio',
    aggregate: 'mean',
    keywords: ['betreuungsschl\u00fcssel', 'schl\u00fcssel', 'personal', 'care ratio', 'staff'],
    chart: 'line',
    deltaInverse: true,
    scope: BOTH,
    adapt: adaptCareRatio,
    detailRoute: 'groups',
  },
  {
    id: 'care_ratio_violations',
    section: 'occupancy',
    label: msg({
      id: 'analytics.metrics.violations',
      message: 'Care-ratio violations',
    }),
    basis: msg({
      id: 'analytics.metrics.violations.basis',
      message: 'Group days below the required care ratio.',
    }),
    path: '/api/v1/cockpit/occupancy/violations',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['verst\u00f6sse', 'verstoss', 'unterbesetzt', 'violations', 'understaffed'],
    chart: 'bar',
    deltaInverse: true,
    scope: BOTH,
    adapt: adaptViolations,
    detailRoute: 'groups',
  },
  // --- Team -----------------------------------------------------------------
  {
    id: 'employee_absence_rates',
    section: 'team',
    label: msg({ id: 'analytics.metrics.sickRate', message: 'Sickness rate' }),
    basis: msg({
      id: 'analytics.metrics.sickRate.basis',
      message:
        'Basis: target hours (Mon\u2013Fri excluding public holidays, weighted by workload)',
    }),
    path: '/api/v1/cockpit/employees/absence-rates',
    unit: 'percent',
    aggregate: 'mean',
    keywords: [
      'krank',
      'krankheit',
      'krankheitsquote',
      'unfall',
      'unfallquote',
      'absenz',
      'absenzquote',
      'kind krank',
      'sick',
      'sickness',
      'accident',
      'absence',
    ],
    chart: 'bar',
    deltaInverse: true,
    scope: BOTH,
    params: { group_by: 'none', compare_to: 'previous_year' },
    adapt: adaptAbsenceRates,
    // The period value is the server's hours-weighted summary (D10).
    summary: adaptAbsenceRatesSummary,
    childSickToggle: true,
    detailRoute: 'employees',
  },
  {
    id: 'employee_absences',
    section: 'team',
    label: msg({ id: 'analytics.metrics.absenceDays', message: 'Absences (days)' }),
    basis: msg({
      id: 'analytics.metrics.absenceDays.basis',
      message:
        'Approved absence days by reason, Mon\u2013Fri, half days as 0.5, public holidays not excluded.',
    }),
    path: '/api/v1/cockpit/employees/absences',
    unit: 'days',
    aggregate: 'sum',
    keywords: ['absenz', 'absenzen', 'ferien', 'krank', 'absence', 'vacation', 'leave'],
    chart: 'bar',
    deltaInverse: true,
    scope: BOTH,
    breakdownLabels: ABSENCE_TYPE_LABELS,
    stacked: true,
    adapt: adaptCockpitEnvelope,
    detailRoute: 'employees',
  },
  {
    id: 'app_usage',
    section: 'team',
    label: msg({ id: 'analytics.metrics.appUsage', message: 'App usage' }),
    basis: msg({
      id: 'analytics.metrics.appUsage.basis',
      message: 'Activities recorded and messages sent by staff in the app.',
    }),
    path: '/api/v1/cockpit/employees/app-usage',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['app', 'nutzung', 'aktivit\u00e4ten', 'nachrichten', 'usage', 'messages'],
    chart: 'bar',
    scope: BOTH,
    breakdownLabels: {
      activities: msg({
        id: 'analytics.metrics.part.activities',
        message: 'Activities',
      }),
      messages: msg({ id: 'analytics.metrics.part.messages', message: 'Messages' }),
    },
    stacked: true,
    adapt: adaptCockpitEnvelopeParts(['activities', 'messages']),
  },
  {
    id: 'activity_minutes',
    section: 'team',
    label: msg({ id: 'analytics.metrics.activityMinutes', message: 'Activity minutes' }),
    basis: msg({
      id: 'analytics.metrics.activityMinutes.basis',
      message:
        'Average minutes per recorded activity, weighted by the number of activities.',
    }),
    path: '/api/v1/cockpit/children/activities',
    unit: 'minutes',
    aggregate: 'mean',
    keywords: ['aktivit\u00e4t', 'minuten', 'dauer', 'activity', 'minutes'],
    chart: 'line',
    scope: BOTH,
    adapt: withoutParts(adaptActivityMinutes),
  },
  // --- Children -------------------------------------------------------------
  {
    id: 'arrival_pickup',
    section: 'children',
    label: msg({ id: 'analytics.metrics.arrivalPickup', message: 'Arrival & pick-up' }),
    basis: msg({
      id: 'analytics.metrics.arrivalPickup.basis',
      message: 'Average minutes between booked and actual arrival or pick-up.',
    }),
    path: '/api/v1/cockpit/children/arrival-pickup',
    unit: 'minutes',
    aggregate: 'mean',
    keywords: ['ankunft', 'abholung', 'bringen', 'abholen', 'arrival', 'pickup'],
    chart: 'line',
    deltaInverse: true,
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    adapt: adaptCockpitEnvelopeNoParts,
  },
  {
    id: 'courses',
    section: 'children',
    label: msg({ id: 'analytics.metrics.courses', message: 'Courses' }),
    basis: msg({
      id: 'analytics.metrics.courses.basisAvg',
      message: 'Course places filled, average of the months.',
    }),
    path: '/api/v1/cockpit/children/courses',
    unit: 'percent',
    aggregate: 'mean',
    keywords: ['kurse', 'kurs', 'courses', 'course'],
    chart: 'line',
    scope: BOTH,
    adapt: adaptCockpitEnvelopeNoParts,
  },
  {
    id: 'child_absences',
    section: 'children',
    label: msg({ id: 'analytics.metrics.childAbsences', message: 'Child absences' }),
    basis: msg({
      id: 'analytics.metrics.childAbsences.basis',
      message: 'Absences reported for children.',
    }),
    path: '/api/v1/cockpit/children/absences',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['kinder', 'abwesend', 'absenz', 'krank', 'child absences'],
    chart: 'bar',
    deltaInverse: true,
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    adapt: adaptCockpitEnvelopeNoParts,
  },
  {
    id: 'portfolio_documentation',
    section: 'children',
    label: msg({
      id: 'analytics.metrics.portfolio',
      message: 'Learning documentation',
    }),
    basis: msg({
      id: 'analytics.metrics.portfolio.basisAvg',
      message: 'Portfolio entries per child, average of the months.',
    }),
    path: '/api/v1/cockpit/children/portfolio-documentation',
    unit: 'decimal',
    aggregate: 'mean',
    keywords: ['portfolio', 'bildungsdokumentation', 'dokumentation', 'documentation'],
    chart: 'line',
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    adapt: adaptCockpitEnvelopeNoParts,
    // 555 ms warm on seed data: loaded only when its section is open (G6).
    lazy: true,
  },
  {
    id: 'enrollment_movement',
    section: 'children',
    label: msg({
      id: 'analytics.metrics.enrollment',
      message: 'Enrolments and leavers',
    }),
    basis: msg({
      id: 'analytics.metrics.enrollment.basis',
      message: 'Children starting minus children leaving.',
    }),
    path: '/api/v1/cockpit/children/enrollment-movement',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['eintritt', 'austritt', 'eintritte', 'austritte', 'enrolment', 'leavers'],
    chart: 'bar',
    scope: NONE,
    breakdownLabels: NEW_ENDED_LABELS,
    adapt: adaptEnrollmentMovement,
  },
  // --- Finance --------------------------------------------------------------
  {
    id: 'outstanding',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.outstanding', message: 'Outstanding' }),
    basis: msg({
      id: 'analytics.metrics.outstanding.basis',
      message: 'Open receivables at each month end, parents and subsidy offices.',
    }),
    path: '/api/v1/reconciliation/monthly-trend',
    unit: 'chf',
    // A month-end balance (C6).
    aggregate: 'last',
    keywords: [
      'ausstehend',
      'offen',
      'debitoren',
      'forderungen',
      'subvention',
      'outstanding',
      'receivables',
      'debtors',
    ],
    // One request: from/to plus the year before (compare_to, #2192).
    params: { compare_to: 'previous_year' },
    yearAgoFromSeries: true,
    chart: 'bar',
    deltaInverse: true,
    scope: NONE,
    breakdownLabels: PAYER_LABELS,
    stacked: true,
    adapt: adaptOutstandingTrend,
    detailRoute: 'debtors',
  },
  {
    id: 'billed',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.billed', message: 'Billed' }),
    basis: msg({
      id: 'analytics.metrics.billed.basis',
      message:
        'Invoiced to parents and subsidy offices per month, from the reconciliation ledger.',
    }),
    path: '/api/v1/reconciliation/monthly-trend',
    unit: 'chf',
    aggregate: 'sum',
    keywords: ['fakturiert', 'rechnungen', 'einnahmen', 'umsatz', 'billed', 'invoiced'],
    params: { compare_to: 'previous_year' },
    yearAgoFromSeries: true,
    chart: 'bar',
    scope: NONE,
    breakdownLabels: PAYER_LABELS,
    stacked: true,
    adapt: adaptBilledTrend,
    detailRoute: 'invoices',
  },
  {
    id: 'revenue',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.revenue', message: 'Revenue' }),
    basis: msg({
      id: 'analytics.metrics.revenue.basis',
      message: 'Invoiced revenue, subsidised and not subsidised.',
    }),
    path: '/api/v1/cockpit/financial/revenue',
    unit: 'chf',
    aggregate: 'sum',
    keywords: ['umsatz', 'einnahmen', 'fakturiert', 'subvention', 'revenue', 'invoiced'],
    chart: 'bar',
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year', split: 'subsidized' },
    breakdownLabels: {
      subsidized: msg({
        id: 'analytics.metrics.part.subsidized',
        message: 'Subsidised',
      }),
      non_subsidized: msg({
        id: 'analytics.metrics.part.nonSubsidized',
        message: 'Not subsidised',
      }),
    },
    stacked: true,
    adapt: adaptCockpitEnvelope,
    detailRoute: 'invoices',
  },
  {
    id: 'contracts',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.contractStarts', message: 'Contract starts' }),
    basis: msg({
      id: 'analytics.metrics.contracts.basis',
      message: 'Contracts starting per month; ended contracts beside them.',
    }),
    path: '/api/v1/cockpit/financial/contracts',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['vertr\u00e4ge', 'vertrag', 'vertragsstarts', 'contracts'],
    chart: 'bar',
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    breakdownLabels: NEW_ENDED_LABELS,
    adapt: adaptContracts,
  },
  {
    id: 'cashin',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.cashin', message: 'Cash in' }),
    basis: msg({
      id: 'analytics.metrics.cashin.basis',
      message: 'Money received on the bank account, parents and subsidy offices.',
    }),
    path: '/api/v1/cockpit/financial/cashin',
    unit: 'chf',
    aggregate: 'sum',
    keywords: [
      'zahlungseingang',
      'eingang',
      'erhalten',
      'bezahlt',
      'cash in',
      'received',
    ],
    chart: 'bar',
    scope: BOTH,
    params: { granularity: 'month' },
    breakdownLabels: PAYER_LABELS,
    stacked: true,
    adapt: adaptCashIn,
  },
  {
    id: 'liquidity',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.liquidity', message: 'Liquidity' }),
    basis: msg({
      id: 'analytics.metrics.liquidity.basis',
      message: 'Expected inflow from running contracts in the coming months.',
    }),
    path: '/api/v1/cockpit/financial/liquidity',
    unit: 'chf',
    // Expected inflow per month is a flow: the twelve months add up (D9a).
    aggregate: 'sum',
    keywords: ['liquidit\u00e4t', 'zufluss', 'liquidity', 'inflow'],
    chart: 'bar',
    scope: BOTH,
    adapt: adaptLiquidity,
    forward: true,
    requests: nextTwelveMonths,
    forwardWindow: msg({
      id: 'analytics.metrics.window.next12',
      message: 'Next 12 months',
    }),
  },
  {
    id: 'payments',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.openByAge', message: 'Open amounts by age' }),
    basis: msg({
      id: 'analytics.metrics.payments.basis',
      message: 'Unpaid invoice amounts at each month end, by age in days.',
    }),
    path: '/api/v1/cockpit/financial/payments',
    unit: 'chf',
    // Unpaid amounts at month end, a balance (C7).
    aggregate: 'last',
    keywords: ['offene betr\u00e4ge', 'alter', '\u00fcberf\u00e4llig', 'mahnung', 'aging', 'overdue'],
    chart: 'bar',
    deltaInverse: true,
    scope: BOTH,
    params: { granularity: 'month', compare_to: 'previous_year' },
    breakdownLabels: AGING_LABELS,
    stacked: true,
    adapt: adaptPayments,
    detailRoute: 'debtors',
  },
  {
    id: 'payment_methods',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.paymentMethods', message: 'Payment methods' }),
    basis: msg({
      id: 'analytics.metrics.paymentMethods.basis',
      message: 'Payments per month by method.',
    }),
    path: '/api/v1/cockpit/financial/payment-methods',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['zahlungsart', 'lastschrift', '\u00fcberweisung', 'bar', 'payment methods'],
    chart: 'bar',
    scope: LOCATION_ONLY,
    breakdownLabels: PAYMENT_METHOD_LABELS,
    stacked: true,
    adapt: adaptPaymentMethods,
  },
  {
    id: 'forecast',
    section: 'finance',
    label: msg({ id: 'analytics.metrics.forecast', message: 'Year-end forecast' }),
    basis: msg({
      id: 'analytics.metrics.forecast.basisYear',
      message:
        'Revenue of the calendar year, running total: invoiced so far plus contracted for the months ahead.',
    }),
    path: '/api/v1/cockpit/financial/forecast',
    unit: 'chf',
    // A running total of the year: its last month is the year end (D9a).
    aggregate: 'last',
    // The fast read: the same year-end rule without the multi-minute engine run.
    params: { actuals_only: true },
    keywords: ['prognose', 'jahresprognose', 'hochrechnung', 'umsatz', 'forecast'],
    requests: calendarYearOf,
    chart: 'bar',
    scope: BOTH,
    breakdownLabels: {
      invoiced: msg({ id: 'analytics.metrics.part.invoiced', message: 'Invoiced' }),
      contracted: msg({
        id: 'analytics.metrics.part.contracted',
        message: 'Contracted',
      }),
    },
    stacked: true,
    adapt: adaptForecastCumulative,
    forward: true,
  },
  // --- Workflows ------------------------------------------------------------
  {
    id: 'processing_time',
    section: 'workflows',
    label: msg({
      id: 'analytics.metrics.processingTime',
      message: 'Task processing time',
    }),
    basis: msg({
      id: 'analytics.metrics.processingTime.basis',
      message: 'Median minutes from a task being raised to being done.',
    }),
    path: '/api/v1/cockpit/workflows/processing-time',
    unit: 'minutes',
    aggregate: 'mean',
    keywords: ['bearbeitungszeit', 'aufgaben', 'dauer', 'processing time', 'tasks'],
    chart: 'line',
    deltaInverse: true,
    scope: NONE,
    adapt: withoutParts(adaptProcessingTime),
  },
  {
    id: 'workflow_items',
    section: 'workflows',
    label: msg({ id: 'analytics.metrics.workflowItems', message: 'Tasks by status' }),
    basis: msg({
      id: 'analytics.metrics.workflowItems.basis',
      message: 'Tasks raised per month; completed and overdue beside them.',
    }),
    path: '/api/v1/cockpit/workflows/items',
    unit: 'count',
    aggregate: 'sum',
    keywords: ['aufgaben', 'pendenzen', 'tasks'],
    chart: 'bar',
    scope: NONE,
    breakdownLabels: {
      created: msg({ id: 'analytics.metrics.part.created', message: 'Raised' }),
      completed: msg({ id: 'analytics.metrics.part.completed', message: 'Completed' }),
      overdue: msg({ id: 'analytics.metrics.part.overdue', message: 'Overdue' }),
    },
    adapt: adaptWorkflowItems,
  },
];

const BY_ID = new Map(METRICS.map((m) => [m.id, m]));

export function getMetric(id: string | undefined): MetricDefinition | undefined {
  return id ? BY_ID.get(id) : undefined;
}

/** The first-run favourites of Meine Kennzahlen (H1). */
export const DEFAULT_FAVOURITE_METRICS: readonly string[] = [
  'utilization',
  'employee_absence_rates',
  'outstanding',
  'care_ratio',
];
